import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCustomReport, getDailyReport, getWeeklyReport,
  getMonthlyReport, getYearlyReport, clearReport,
} from '../../redux/features/sales/reportSlice';
import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser";
import { useRedirectEmployee } from "../../customHook/useRedirectEmploye";
import { calculateYearlyTax, scaleProfit } from '../../utils/taxUtils';
import ReactToPrint from 'react-to-print';
import ButtonLoading from '../../loader/ButtonLoader';
import { EthiopianDateCell } from './EthiopianDateCell';
import Select from 'react-select';
import {
  gregorianToEth,
  formatEthiopianDate,
  getEthiopianYearOptions,
  ETHIOPIAN_MONTH_OPTIONS,
  getEthiopianWeekOptions,
  getEthBillingMonthRange,
  getEthYearRange,
  getEthiopianDayOptions,
  ethToGregorian,
} from '../../utils/ethiopianCalendar';
import {
  EthiopianDatePicker,
  EthiopianDateRangePicker,
} from '../../utils/EthiopianDatePicker';
import { DualDateRangeInput } from '../DualDateInput';

// Add near the top of Reports.jsx with other helpers
const todayGcStr = () => {
  const d = new Date();
  const y  = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
};

const parseGcDate = (str) => {
  if (!str) return new Date();
  const [y, m, d] = str.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const dash = (v) => (v === null || v === undefined || v === "") ? "—" : v;
const fmt  = (n) => Number(n || 0).toLocaleString();

const selectStyles = {
  control: (b) => ({ ...b, minHeight: "46px", fontSize: "1.4rem" }),
  menu:    (b) => ({ ...b, zIndex: 9999,       fontSize: "1.4rem" }),
};

// ─── Section table ─────────────────────────────────────────────
const SectionTable = ({ title, color = "primary", badge, children, summary }) => (
  <div className="mb-5">
    <div className={`d-flex align-items-center justify-content-between px-4 py-3
                     rounded-top-3 bg-${color} bg-opacity-10 border border-bottom-0`}>
      <div className="d-flex align-items-center gap-2">
        <h3 className="fw-semibold mb-0">{title}</h3>
        {badge && (
          <span className={`badge fs-5 bg-${color} rounded-pill px-3`}>{badge}</span>
        )}
      </div>
      {summary && <div className="d-flex gap-3 fs-4">{summary}</div>}
    </div>
    <div className="table-responsive border rounded-bottom-3 fs-4">
      {children}
    </div>
  </div>
);

const SummaryBadge = ({ label, value, color = "secondary" }) => (
  <div className="text-end">
    <p className="text-muted mb-0 fs-4">{label}</p>
    <span className={`fw-semibold fs-4 text-${color}`}>{value}</span>
  </div>
);

// ─── Period label ──────────────────────────────────────────────
const buildPeriodLabel = (period, reportData, periodMeta) => {
  if (!reportData?.period) return "";

  if (period === "monthly" && reportData.period.label) {
    return reportData.period.label;
  }

  const s = formatEthiopianDate(reportData.period.startDate);
  const e = formatEthiopianDate(reportData.period.endDate);

  if (period === "daily")   return `${s}`;
  if (period === "weekly")  return `Week: ${s} – ${e}`;
  if (period === "monthly") return `${s} – ${e}`;
  if (period === "yearly")  return `Ethiopian Year ${periodMeta?.ethYear || ""} E.C.`;
  return `${s} – ${e}`;
};


const Reports = () => {
  useRedirectLogOutUser();
  useRedirectEmployee();

  const dispatch  = useDispatch();
  const printRef  = useRef();

  const { reportData, activePeriod, isLoading } =
    useSelector((state) => state.report);

    console.log("Active period:", reportData);

  // ── Active tab ────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("daily");

  // ── Period selection state ─────────────────────────────────
  const todayEth    = useMemo(() => gregorianToEth(new Date()), []);
  const yearOptions = useMemo(() => getEthiopianYearOptions(), []);
  const dayOptions  = useMemo(() => getEthiopianDayOptions(),  []);

  const [selectedDay,   setSelectedDay]   = useState(dayOptions[0]);    // today
  const [selectedYear,  setSelectedYear]  = useState(
    yearOptions.find(y => y.value === todayEth.year) || yearOptions[0]
  );
  const [selectedMonth, setSelectedMonth] = useState(
    ETHIOPIAN_MONTH_OPTIONS.find(m => m.value === todayEth.month)
  );
  const [selectedWeek,  setSelectedWeek]  = useState(null);

  // Week options depend on selected year
  const weekOptions = useMemo(() =>
    getEthiopianWeekOptions(selectedYear?.value || todayEth.year),
  [selectedYear]);

  // Auto-select current week when week options load
  useEffect(() => {
    if (weekOptions.length > 0 && !selectedWeek) {
      setSelectedWeek(weekOptions[0]); // most recent week first
    }
  }, [weekOptions]);

  // Custom date range (in Gregorian for the input, display in Ethiopian)
  const [customStart, setCustomStart] = useState("");
  const [customEnd,   setCustomEnd]   = useState("");
  // Ethiopian display for custom
  const customStartEth = customStart
    ? formatEthiopianDate(new Date(customStart)) : "";
  const customEndEth   = customEnd
    ? formatEthiopianDate(new Date(customEnd))   : "";

  // ── Period meta (for label) ────────────────────────────────
 const [periodMeta, setPeriodMeta] = useState({
  label:     "",
  startDate: null,
  endDate:   null,
});

const displayLabel = periodMeta.label || "";


  // ── Tax ───────────────────────────────────────────────────
  const tax = useMemo(() => {
    if (!reportData?.summary) return null;
    const profit       = reportData.summary.netProfit;
    const annualProfit = scaleProfit(profit, activePeriod);
    return { ...calculateYearlyTax(annualProfit), annualProfit, actualProfit: profit };
  }, [reportData, activePeriod]);

  // ── Fetch helpers ──────────────────────────────────────────

  const fetchDaily = (dayOpt) => {
  if (!dayOpt) return;

  // ✅ Use plain date strings — no timezone conversion
  const dateStr = dayOpt.value; // already "YYYY-MM-DD"
  const ethLabel = formatEthiopianDate(new Date(dateStr + "T12:00:00"));
  // use noon to avoid any date boundary issues

  setPeriodMeta({
    label:     ethLabel,
    startDate: dateStr,
    endDate:   dateStr,
  });

  dispatch(getCustomReport({
    startDate: dateStr,
    endDate:   dateStr,
  }));
};

  const fetchWeekly = (weekOpt) => {
  if (!weekOpt) return;

  const s = weekOpt.startDate.toISOString().split("T")[0];
  const e = weekOpt.endDate.toISOString().split("T")[0];

  const sLabel = formatEthiopianDate(new Date(s + "T12:00:00"));
  const eLabel = formatEthiopianDate(new Date(e + "T12:00:00"));

  setPeriodMeta({
    label:     `${sLabel} – ${eLabel}`,
    startDate: s,
    endDate:   e,
  });

  dispatch(getCustomReport({ startDate: s, endDate: e }));
};

  const fetchMonthly = (year, month) => {
  if (!year || !month) return;

  const { startDate, endDate, label } =
    getEthBillingMonthRange(year.value, month.value);

  const s = startDate.toISOString().split("T")[0];
  const e = endDate.toISOString().split("T")[0];

  setPeriodMeta({ label, startDate: s, endDate: e });

  dispatch(getCustomReport({ startDate: s, endDate: e }));
};

  const fetchYearly = (year) => {
  if (!year) return;

  const { startDate, endDate } = getEthYearRange(year.value);

  const s = startDate.toISOString().split("T")[0];
  const e = endDate.toISOString().split("T")[0];

  const sLabel = formatEthiopianDate(new Date(s + "T12:00:00"));
  const eLabel = formatEthiopianDate(new Date(e + "T12:00:00"));

  setPeriodMeta({
    label:     `Ethiopian Year ${year.value} E.C. (${sLabel} – ${eLabel})`,
    startDate: s,
    endDate:   e,
  });

  dispatch(getCustomReport({ startDate: s, endDate: e }));
};
  const fetchCustom = (e) => {
  e.preventDefault();
  if (!customStart || !customEnd) return;

  const sLabel = formatEthiopianDate(new Date(customStart + "T12:00:00"));
  const eLabel = formatEthiopianDate(new Date(customEnd   + "T12:00:00"));

  setPeriodMeta({
    label:     `${sLabel} – ${eLabel}`,
    startDate: customStart,
    endDate:   customEnd,
  });

  dispatch(getCustomReport({ startDate: customStart, endDate: customEnd }));
};

  // Auto-fetch when tab switches
  useEffect(() => {
    dispatch(clearReport());
    if (activeTab === "daily"   && selectedDay)   fetchDaily(selectedDay);
    if (activeTab === "weekly"  && selectedWeek)  fetchWeekly(selectedWeek);
    if (activeTab === "monthly" && selectedYear && selectedMonth)
      fetchMonthly(selectedYear, selectedMonth);
    if (activeTab === "yearly"  && selectedYear)  fetchYearly(selectedYear);
  }, [activeTab]);

  const { summary } = reportData || {};
  console.log("Report data:", summary);

  const periodTabs = [
    { key: "daily",   label: "📅 Daily"   },
    { key: "weekly",  label: "📆 Weekly"  },
    { key: "monthly", label: "🗓 Monthly" },
    { key: "yearly",  label: "📊 Yearly"  },
    { key: "custom",  label: "📋 Custom"  },
  ];

  return (
    <div className="bg-light min-vh-100 p-4">

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="fw-bold mb-1">Reports</h2>
          <p className="text-muted mb-0 fs-5">
            {reportData ? displayLabel : "Select a period"}
          </p>
        </div>
        {reportData && (
          <ReactToPrint
            trigger={() => (
              <button className="btn btn-primary rounded-3">
                🖨️ Print Report
              </button>
            )}
            content={() => printRef.current}
            pageStyle="@page { size: A4; margin: 15mm; }"
          />
        )}
      </div>

      {/* ── Tabs ────────────────────────────────────────────── */}
      <div className="d-flex gap-2 flex-wrap mb-4">
        {periodTabs.map(tab => (
          <button key={tab.key}
            className={`btn rounded-3 fw-medium fs-4 ${
              activeTab === tab.key ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => {
              setActiveTab(tab.key);
              dispatch(clearReport());
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Period Selector ──────────────────────────────────── */}
      <div className="card border-0 shadow-sm rounded-4 mb-4 p-4">
        <div className="row g-3 align-items-end">

          {/* ── DAILY ── */}
          {activeTab === "daily" && (
            <>
              <div className="col-12 col-md-5">
                <label className="fs-4 fw-medium mb-1 d-block">
                  Select Day (Ethiopian Calendar)
                </label>
                <Select
                  options={dayOptions}
                  value={selectedDay}
                  onChange={(v) => {
                    setSelectedDay(v);
                    dispatch(clearReport());
                    fetchDaily(v);
                  }}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-3">
                <div className="p-3 bg-light rounded-3">
                  <p className="text-muted mb-0 fs-5">Gregorian</p>
                  <p className="fw-semibold mb-0 fs-4">
                    {selectedDay
                      ? new Date(selectedDay.value).toLocaleDateString("en-GB",{
                          day:"numeric",month:"short",year:"numeric"
                        })
                      : "—"}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* ── WEEKLY ── */}
          {activeTab === "weekly" && (
            <>
              <div className="col-12 col-md-3">
                <label className="fs-4 fw-medium mb-1 d-block">
                  Ethiopian Year
                </label>
                <Select
                  options={yearOptions}
                  value={selectedYear}
                  onChange={(v) => {
                    setSelectedYear(v);
                    setSelectedWeek(null);
                    // weekOptions will update via useMemo
                  }}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="fs-4 fw-medium mb-1 d-block">
                  Select Week
                </label>
                <Select
                  options={weekOptions}
                  value={selectedWeek}
                  onChange={(v) => {
                    setSelectedWeek(v);
                    dispatch(clearReport());
                    fetchWeekly(v);
                  }}
                  placeholder="Select week..."
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-3 d-flex align-items-end">
                <button className="btn btn-primary rounded-3 w-100 fs-4"
                  onClick={() => fetchWeekly(selectedWeek)}
                  disabled={!selectedWeek || isLoading}>
                  {isLoading ? "Loading..." : "Load Week"}
                </button>
              </div>
            </>
          )}

          {/* ── MONTHLY ── */}
          {activeTab === "monthly" && (
            <>
              <div className="col-12 col-md-3">
                <label className="fs-4 fw-medium mb-1 d-block">
                  Ethiopian Year
                </label>
                <Select
                  options={yearOptions}
                  value={selectedYear}
                  onChange={setSelectedYear}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="fs-4 fw-medium mb-1 d-block">
                  Ethiopian Month
                </label>
                <Select
                  options={ETHIOPIAN_MONTH_OPTIONS}
                  value={selectedMonth}
                  onChange={setSelectedMonth}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-2 d-flex align-items-end">
                <button className="btn btn-primary rounded-3 w-100 fs-4"
                  onClick={() => fetchMonthly(selectedYear, selectedMonth)}
                  disabled={!selectedYear || !selectedMonth || isLoading}>
                  {isLoading ? "Loading..." : "Load Month"}
                </button>
              </div>
              {/* Show the billing range */}
              {selectedYear && selectedMonth && (
                <div className="col-12">
                  <div className="alert alert-info py-2 px-3 mb-0 fs-3">
                    📅 Billing period:{" "}
                    <strong>
                      {getEthBillingMonthRange(
                        selectedYear.value,
                        selectedMonth.value
                      ).label}
                    </strong>
                    <span className="text-muted ms-2">
                      (21st–20th Ethiopian billing cycle)
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── YEARLY ── */}
          {activeTab === "yearly" && (
            <>
              <div className="col-12 col-md-4">
                <label className="fs-4 fw-medium mb-1 d-block">
                  Ethiopian Year
                </label>
                <Select
                  options={yearOptions}
                  value={selectedYear}
                  onChange={setSelectedYear}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-3 d-flex align-items-end">
                <button className="btn btn-primary rounded-3 w-100 fs-4"
                  onClick={() => fetchYearly(selectedYear)}
                  disabled={!selectedYear || isLoading}>
                  {isLoading ? "Loading..." : "Load Year"}
                </button>
              </div>
              {selectedYear && (
                <div className="col-12 col-md-5 d-flex align-items-end">
                  <div className="p-3 bg-light rounded-3 w-100">
                    <p className="text-muted mb-0 fs-5">Gregorian equivalent</p>
                    <p className="fw-semibold mb-0 fs-4">
                      {(() => {
                        const { startDate, endDate } =
                          getEthYearRange(selectedYear.value);
                        return `${startDate.toLocaleDateString("en-GB", {
                          day:"numeric",month:"short",year:"numeric"
                        })} – ${endDate.toLocaleDateString("en-GB", {
                          day:"numeric",month:"short",year:"numeric"
                        })}`;
                      })()}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── CUSTOM ── */}
          {activeTab === "custom" && (
            <div className="col-12">
              <DualDateRangeInput
                startDate={customStart || todayGcStr()}
                endDate={customEnd     || todayGcStr()}
                onStartChange={(v) => setCustomStart(v)}
                onEndChange={(v)   => setCustomEnd(v)}
                required
                isLoading={isLoading}
                showButton
                onSubmit={() => {           {/* ✅ call fetchCustom directly */}
                  if (!customStart && !customEnd) {
                    const today = todayGcStr();
                    setCustomStart(today);
                    setCustomEnd(today);
                    dispatch(getCustomReport({
                      startDate: today,
                      endDate:   today,
                    }));
                    return;
                  }
                  const start = customStart || todayGcStr();
                  const end   = customEnd   || todayGcStr();
                  const sLabel = formatEthiopianDate(parseGcDate(start));
                  const eLabel = formatEthiopianDate(parseGcDate(end));
                  setPeriodMeta({
                    label:     `${sLabel} – ${eLabel}`,
                    startDate: start,
                    endDate:   end,
                  });
                  dispatch(getCustomReport({
                    startDate: start,
                    endDate:   end,
                  }));
                }}
              />
            </div>
          )}

        </div>
      </div>

      {/* ── Loading ──────────────────────────────────────────── */}
      {isLoading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
          <p className="text-muted mt-2">Generating report...</p>
        </div>
      )}

      {/* ── No data ──────────────────────────────────────────── */}
      {!isLoading && !reportData && (
        <div className="text-center py-5">
          <p className="text-muted fs-4">
            No data available for this period.
          </p>
        </div>
      )}

      {/* ── REPORT OUTPUT ─────────────────────────────────────── */}
      {reportData && !isLoading && (
        <div ref={printRef}>

          {/* Period header for print */}
          <div className="d-none d-print-block mb-4">
            <h3>Megertu Ceramics — Report</h3>
            <p>{buildPeriodLabel(activePeriod, reportData, periodMeta)}</p>
          </div>

          {/* ── Summary Cards ─────────────────────────────── */}
          <div className="row g-3 mb-5 mt-5">
            {[
              { label: "Sales Revenue",      value: `${fmt(summary.totalSalesRevenue)} birr`, color: "success", icon: "💰" },
              { label: "Cost of Goods Sold", value: `${fmt(summary.totalCOGS)} birr`,         color: "primary", icon: "🏷️" },
              { label: "Gross Profit",       value: `${fmt(summary.grossProfit)} birr`,        color: summary.grossProfit >= 0 ? "success" : "danger", icon: "📊" },
              { label: "Total Expenses",     value: `${fmt(summary.totalExpenses)} birr`,      color: "warning", icon: "🧾" },
              { label: "Adjustment Losses",  value: `${fmt(summary.totalAdjustmentLoss)} birr`,color: "danger",  icon: "⚠️" },
              { label: "Net Profit",         value: `${fmt(summary.netProfit)} birr`,          color: summary.netProfit >= 0 ? "success" : "danger", icon: summary.netProfit >= 0 ? "📈" : "📉" },
            ].map((card, i) => (
              <div key={i} className="col-6 col-md-4 col-lg-2">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body px-3 py-3">
                    <p className="text-muted mb-1 fw-semibold text-uppercase fs-4"
                      style={{ letterSpacing: "0.05em" }}>
                      {card.label}
                    </p>
                    <p className="mb-0">
                      <span style={{ fontSize: "1.3rem" }}>{card.icon} </span>
                      <span className={`fw-bold fs-4 text-${card.color}`}>
                        {card.value}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── 1. Sales ──────────────────────────────────── */}
          <SectionTable
            title="Sales"
            color="success"
            badge={`${summary.salesCount} transactions`}
            summary={
              <>
                <SummaryBadge label="Revenue" value={`${fmt(summary.totalSalesRevenue)} birr`} color="success" />
                <SummaryBadge label="Profit"  value={`${fmt(summary.totalSalesProfit)} birr`}  color="success" />
                <SummaryBadge label="Unpaid"  value={`${fmt(summary.totalUnpaid)} birr`}        color="danger"  />
              </>
            }>
            <table className="table table-hover table-striped mb-0 fs-4">
              <thead>
                <tr style={{ backgroundColor: "#f0fff4" }}>
                  <th className="px-3 py-3">No</th>
                  <th className="px-3 py-3">Items</th>
                  <th className="px-3 py-3">Customer</th>
                  <th className="px-3 py-3">Payment</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3 text-end">Total</th>
                  <th className="px-3 py-3 text-end">Paid</th>
                  <th className="px-3 py-3 text-end">Unpaid</th>
                  <th className="px-3 py-3 text-end">Profit</th>
                  <th className="px-3 py-3">Added By</th>
                  <th className="px-3 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {reportData.sales.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center text-muted py-4">
                      No sales in this period.
                    </td>
                  </tr>
                ) : reportData.sales.map((sale, i) => {
                  const saleProfit = sale.items.reduce(
                    (s, item) => s + (item.profit || 0), 0
                  );
                  return (
                    <tr key={sale._id}>
                      <td className="px-3 py-2">{i + 1}</td>
                      <td className="px-3 py-2">
                        <div className="d-flex flex-wrap gap-1">
                          {sale.items.map((item, j) => (
                            <span key={j}
                              className="badge bg-success bg-opacity-10
                                         text-success rounded-pill px-2 py-1 fs-5">
                              {item.product?.name || "—"} × {item.quantity}{" "}
                              {item.product?.category?.measurementUnit || "pcs"}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {dash(sale.customer?.name || sale.customerName)}
                      </td>
                      <td className="px-3 py-2">{dash(sale.paymentMethod)}</td>
                      <td className="px-3 py-2">
                        <span className={`badge rounded-pill px-2 py-1 bg-${
                          sale.paymentStatus === "paid"    ? "success" :
                          sale.paymentStatus === "partial" ? "warning" : "danger"
                        }`}>{sale.paymentStatus}</span>
                      </td>
                      <td className="px-3 py-2 text-end fw-semibold">
                        {fmt(sale.totalAmount)}
                      </td>
                      <td className="px-3 py-2 text-end text-success">
                        {fmt(sale.paidAmount)}
                      </td>
                      <td className="px-3 py-2 text-end text-danger">
                        {sale.unpaidAmount > 0 ? fmt(sale.unpaidAmount) : "—"}
                      </td>
                      <td className="px-3 py-2 text-end text-success fw-semibold">
                        {fmt(saleProfit)}
                      </td>
                      <td className="px-3 py-2">
                        {dash(sale.addedBy?.firstname)}
                      </td>
                      <td className="px-3 py-2">
                        <EthiopianDateCell value={sale.createdAt} dual />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {reportData.sales.length > 0 && (
                <tfoot>
                  <tr className="table-success fw-bold">
                    <td colSpan="5" className="px-3 py-2">TOTALS</td>
                    <td className="px-3 py-2 text-end">
                      {fmt(summary.totalSalesRevenue)}
                    </td>
                    <td className="px-3 py-2 text-end">
                      {fmt(summary.totalSalesRevenue - summary.totalUnpaid)}
                    </td>
                    <td className="px-3 py-2 text-end text-danger">
                      {fmt(summary.totalUnpaid)}
                    </td>
                    <td className="px-3 py-2 text-end">
                      {fmt(summary.totalSalesProfit)}
                    </td>
                    <td colSpan="2" />
                  </tr>
                </tfoot>
              )}
            </table>
          </SectionTable>

          {/* ── 2. Stock Purchased ────────────────────────── */}
          <SectionTable
            title="Stock Purchased (Product Batches)"
            color="primary"
            badge={`${summary.batchesCount} batches`}
            summary={
              <SummaryBadge
                label="Total Cost"
                value={`${fmt(summary.totalStockCost)} birr`}
                color="primary"
              />
            }>
            <table className="table table-hover table-striped mb-0 fs-4">
              <thead>
                <tr style={{ backgroundColor: "#e7f1ff" }}>
                  <th className="px-3 py-3">No</th>
                  <th className="px-3 py-3">Product</th>
                  <th className="px-3 py-3">SKU</th>
                  <th className="px-3 py-3">Category</th>
                  <th className="px-3 py-3 text-end">Qty</th>
                  <th className="px-3 py-3 text-end">Purchase Price</th>
                  <th className="px-3 py-3 text-end">Total Cost</th>
                  <th className="px-3 py-3 text-end">Remaining</th>
                  <th className="px-3 py-3">Supplier</th>
                  <th className="px-3 py-3">Added By</th>
                  <th className="px-3 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {reportData.batches.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center text-muted py-4">
                      No stock purchased in this period.
                    </td>
                  </tr>
                ) : reportData.batches.map((batch, i) => (
                  <tr key={batch._id}>
                    <td className="px-3 py-2">{i + 1}</td>
                    <td className="px-3 py-2 fw-medium">
                      {dash(batch.product?.name)}
                    </td>
                    <td className="px-3 py-2">{dash(batch.product?.sku)}</td>
                    <td className="px-3 py-2">
                      {dash(batch.product?.category?.name)}
                    </td>
                    <td className="px-3 py-2 text-end">
                      {fmt(batch.quantity)}{" "}
                      {batch.product?.category?.measurementUnit === "linear_meter"
                        ? "lm" : dash(batch.product?.category?.measurementUnit)}
                    </td>
                    <td className="px-3 py-2 text-end">
                      {fmt(batch.purchasePrice)}
                    </td>
                    <td className="px-3 py-2 text-end fw-semibold text-primary">
                      {fmt(batch.totalCost)}
                    </td>
                    <td className="px-3 py-2 text-end">
                      {fmt(batch.remainingQuantity)}
                    </td>
                    <td className="px-3 py-2">{dash(batch.supplier)}</td>
                    <td className="px-3 py-2">
                      {dash(batch.addedBy?.firstname)}
                    </td>
                    <td className="px-3 py-2">
                      <EthiopianDateCell value={batch.createdAt} dual />
                    </td>
                  </tr>
                ))}
              </tbody>
              {reportData.batches.length > 0 && (
                <tfoot>
                  <tr className="table-primary fw-bold">
                    <td colSpan="6" className="px-3 py-2">TOTAL STOCK COST</td>
                    <td className="px-3 py-2 text-end">
                      {fmt(summary.totalStockCost)}
                    </td>
                    <td colSpan="4" />
                  </tr>
                </tfoot>
              )}
            </table>
          </SectionTable>

          {/* ── 3. Stock Adjustments ──────────────────────── */}
          <SectionTable
            title="Stock Adjustments (Damaged / Lost)"
            color="warning"
            badge={`${summary.adjustmentCount} records`}
            summary={
              <SummaryBadge
                label="Total Loss"
                value={`${fmt(summary.totalAdjustmentLoss)} birr`}
                color="warning"
              />
            }>
            <table className="table table-hover table-striped mb-0 fs-4">
              <thead>
                <tr style={{ backgroundColor: "#fff8e7" }}>
                  <th className="px-3 py-3">No</th>
                  <th className="px-3 py-3">Product</th>
                  <th className="px-3 py-3">SKU</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3 text-end">Qty</th>
                  <th className="px-3 py-3 text-end">Unit Cost</th>
                  <th className="px-3 py-3 text-end">Total Loss</th>
                  <th className="px-3 py-3">Recoverable</th>
                  <th className="px-3 py-3">Reason</th>
                  <th className="px-3 py-3">Adjusted By</th>
                  <th className="px-3 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {reportData.adjustments.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center text-muted py-4">
                      No adjustments in this period.
                    </td>
                  </tr>
                ) : reportData.adjustments.map((adj, i) => {
                  const unitCost  = adj.productBatch?.purchasePrice || 0;
                  const totalLoss = unitCost * adj.quantity;
                  return (
                    <tr key={adj._id}>
                      <td className="px-3 py-2">{i + 1}</td>
                      <td className="px-3 py-2 fw-medium">
                        {dash(adj.product?.name)}
                      </td>
                      <td className="px-3 py-2">{dash(adj.product?.sku)}</td>
                      <td className="px-3 py-2">
                        <span className={`badge rounded-pill px-2 py-1 bg-${
                          adj.type === "lost"    ? "danger"  :
                          adj.type === "damaged" ? "warning" : "secondary"
                        }`}>{adj.type}</span>
                      </td>
                      <td className="px-3 py-2 text-end">
                        {fmt(adj.quantity)}{" "}
                        {adj.product?.category?.measurementUnit === "linear_meter"
                          ? "lm"
                          : dash(adj.product?.category?.measurementUnit)}
                      </td>
                      <td className="px-3 py-2 text-end">{fmt(unitCost)}</td>
                      <td className="px-3 py-2 text-end fw-semibold text-danger">
                        {fmt(totalLoss)}
                      </td>
                      <td className="px-3 py-2">
                        {adj.isRecoverable
                          ? <span className="badge bg-success rounded-pill px-2">Yes</span>
                          : <span className="text-muted">No</span>}
                      </td>
                      <td className="px-3 py-2">{dash(adj.reason)}</td>
                      <td className="px-3 py-2">
                        {dash(adj.adjustedBy?.firstname)}
                      </td>
                      <td className="px-3 py-2">
                        <EthiopianDateCell value={adj.createdAt} dual />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {reportData.adjustments.length > 0 && (
                <tfoot>
                  <tr className="table-warning fw-bold">
                    <td colSpan="6" className="px-3 py-2">
                      TOTAL ADJUSTMENT LOSS
                    </td>
                    <td className="px-3 py-2 text-end">
                      {fmt(summary.totalAdjustmentLoss)}
                    </td>
                    <td colSpan="4" />
                  </tr>
                </tfoot>
              )}
            </table>
          </SectionTable>

          {/* ── 4. Expenses ───────────────────────────────── */}
          <SectionTable
            title="Expenses"
            color="danger"
            badge={`${summary.expensesCount} records`}
            summary={
              <>
                <SummaryBadge label="Cash"  value={`${fmt(summary.cashExpenses)} birr`}  color="danger" />
                <SummaryBadge label="Bank"  value={`${fmt(summary.bankExpenses)} birr`}  color="danger" />
                <SummaryBadge label="Total" value={`${fmt(summary.totalExpenses)} birr`} color="danger" />
              </>
            }>
            <table className="table table-hover table-striped mb-0 fs-4">
              <thead>
                <tr style={{ backgroundColor: "#fff0f0" }}>
                  <th className="px-3 py-3">No</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3 text-end">Amount</th>
                  <th className="px-3 py-3">Method</th>
                  <th className="px-3 py-3">Description</th>
                  <th className="px-3 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {reportData.expenses.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No expenses in this period.
                    </td>
                  </tr>
                ) : reportData.expenses.map((exp, i) => (
                  <tr key={exp._id}>
                    <td className="px-3 py-2">{i + 1}</td>
                    <td className="px-3 py-2 fw-medium">{dash(exp.type)}</td>
                    <td className="px-3 py-2 text-end fw-semibold text-danger">
                      {fmt(exp.amount)}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`badge rounded-pill px-2 py-1 ${
                        exp.expenseMethod === "Cash"
                          ? "bg-success" : "bg-primary"
                      }`}>{exp.expenseMethod}</span>
                    </td>
                    <td className="px-3 py-2">{dash(exp.description)}</td>
                    <td className="px-3 py-2">
                      <EthiopianDateCell value={exp.createdAt} dual />
                    </td>
                  </tr>
                ))}
              </tbody>
              {reportData.expenses.length > 0 && (
                <tfoot>
                  <tr className="table-danger fw-bold">
                    <td colSpan="2" className="px-3 py-2">TOTAL EXPENSES</td>
                    <td className="px-3 py-2 text-end">
                      {fmt(summary.totalExpenses)}
                    </td>
                    <td colSpan="3" />
                  </tr>
                </tfoot>
              )}
            </table>
          </SectionTable>

          {/* ── 5. Top / Least Selling ────────────────────── */}
          {(reportData.topSelling?.length > 0 ||
            reportData.leastSelling?.length > 0) && (
            <div className="row g-4 mb-5">
              <div className="col-12 col-md-6">
                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-header bg-success bg-opacity-10
                                  border-0 rounded-top-4 px-4 py-3">
                    <h5 className="fw-semibold mb-0">
                      🏆 Top 5 Selling Products
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-hover mb-0 fs-4">
                      <thead>
                        <tr>
                          <th className="px-4 py-2">Rank</th>
                          <th className="px-4 py-2">Product</th>
                          <th className="px-4 py-2 text-end">Qty Sold</th>
                          <th className="px-4 py-2 text-end">Revenue</th>
                          <th className="px-4 py-2 text-end">Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.topSelling.map((p, i) => (
                          <tr key={p.productId}>
                            <td className="px-4 py-2">
                              <span className="fw-bold text-warning">
                                {["🥇","🥈","🥉","4","5"][i]}
                              </span>
                            </td>
                            <td className="px-4 py-2 fw-medium">{p.name}</td>
                            <td className="px-4 py-2 text-end">
                              {fmt(p.qty)} {dash(p.unit)}
                            </td>
                            <td className="px-4 py-2 text-end text-success">
                              {fmt(p.revenue)}
                            </td>
                            <td className="px-4 py-2 text-end text-success fw-semibold">
                              {fmt(p.profit)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-header bg-danger bg-opacity-10
                                  border-0 rounded-top-4 px-4 py-3">
                    <h5 className="fw-semibold mb-0">
                      📉 Least 5 Selling Products
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-hover mb-0 fs-4">
                      <thead>
                        <tr>
                          <th className="px-4 py-2">Rank</th>
                          <th className="px-4 py-2">Product</th>
                          <th className="px-4 py-2 text-end">Qty Sold</th>
                          <th className="px-4 py-2 text-end">Revenue</th>
                          <th className="px-4 py-2 text-end">Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.leastSelling.map((p, i) => (
                          <tr key={p.productId}>
                            <td className="px-4 py-2">
                              <span className="text-muted fw-semibold">
                                {i + 1}
                              </span>
                            </td>
                            <td className="px-4 py-2 fw-medium">{p.name}</td>
                            <td className="px-4 py-2 text-end">
                              {fmt(p.qty)} {dash(p.unit)}
                            </td>
                            <td className="px-4 py-2 text-end">
                              {fmt(p.revenue)}
                            </td>
                            <td className="px-4 py-2 text-end">
                              {fmt(p.profit)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── 6. Profit & Tax ───────────────────────────── */}
          {tax && (
            <div className="card border-0 shadow-sm rounded-4 mb-5">
              <div className={`card-header border-0 rounded-top-4 px-4 py-3
                               bg-${summary.netProfit >= 0
                                 ? "success" : "danger"} bg-opacity-10`}>
                <h3 className="fw-semibold mb-0">
                  {summary.netProfit >= 0 ? "📈" : "📉"} Profit & Estimated Tax
                </h3>
              </div>
              <div className="card-body px-4 py-4">
                <div className="row g-4">

                  <div className="col-12 col-md-6">
                    <h4 className="fw-semibold text-muted text-uppercase mb-3"
                      style={{ fontSize: "1.25rem", letterSpacing: "0.05em" }}>
                      Profit Breakdown
                    </h4>
                    {[
                      { label: "Sales Revenue",      value:  summary.totalSalesRevenue,    color: "success", sign: "+" },
                      { label: "Cost of Goods Sold", value: -summary.totalCOGS,            color: "danger",  sign: "−" },
                      { label: "Gross Profit",       value:  summary.grossProfit,          color: summary.grossProfit >= 0 ? "success" : "danger", sign: "" },
                      { label: "Operating Expenses", value: -summary.totalExpenses,        color: "danger",  sign: "−" },
                      { label: "Adjustment Losses",  value: -summary.totalAdjustmentLoss,  color: "danger",  sign: "−" },
                    ].map((row, i) => (
                      <div key={i}
                        className={`d-flex justify-content-between
                          align-items-center py-2 border-bottom ${
                          row.label === "Gross Profit"
                            ? "bg-light rounded-2 px-2" : ""}`}>
                        <span className="text-muted fs-4">{row.label}</span>
                        <span className={`fw-semibold text-${row.color}`}>
                          {row.sign} {fmt(Math.abs(row.value))} birr
                        </span>
                      </div>
                    ))}
                    <div className="d-flex justify-content-between
                                    align-items-center py-3 mt-1">
                      <span className="fw-bold fs-4">Net Profit</span>
                      <span className={`fw-bold fs-4 text-${
                        summary.netProfit >= 0 ? "success" : "danger"
                      }`}>
                        {summary.netProfit >= 0 ? "+" : ""}
                        {fmt(summary.netProfit)} birr
                      </span>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <h4 className="fw-semibold text-muted text-uppercase mb-3"
                      style={{ fontSize: "1.25rem", letterSpacing: "0.05em" }}>
                      Estimated Tax (Ethiopian Income Tax)
                    </h4>
                    {activePeriod !== "yearly" && (
                      <div className="alert alert-info py-2 px-3 mb-3 fs-5">
                        ℹ️ Tax estimated by annualizing {activePeriod} profit
                        ({fmt(tax.actualProfit)} birr ×{" "}
                        {activeTab === "daily"   ? "365" :
                         activeTab === "weekly"  ? "52"  :
                         activeTab === "monthly" ? "12"  : "1"} ={" "}
                        {fmt(tax.annualProfit)} birr/year)
                      </div>
                    )}
                    <div className="bg-light rounded-3 px-4 py-3">
                      {[
                        { label: "Annual Profit (projected)", value: `${fmt(tax.annualProfit)} birr` },
                        { label: "Tax Bracket",               value: tax.range },
                        { label: "Tax Rate",                  value: `${tax.percentage}%` },
                        { label: "Estimated Annual Tax",      value: `${fmt(Math.round(tax.tax))} birr`, bold: true, color: "danger" },
                        { label: "Net After Tax (annual)",    value: `${fmt(Math.round(tax.netAfterTax))} birr`, bold: true, color: "success" },
                      ].map((row, i) => (
                        <div key={i}
                          className="d-flex justify-content-between
                                     align-items-center py-2 border-bottom fs-5">
                          <span className="text-muted fs-5">{row.label}</span>
                          <span className={`${row.bold ? "fw-bold" : "fw-semibold"}
                                           ${row.color ? `text-${row.color}` : ""}`}>
                            {row.value}
                          </span>
                        </div>
                      ))}
                    </div>
                    {tax.percentage === 0 && (
                      <p className="text-success mt-2 mb-0 fs-4">
                        ✅ Profit is within the tax-free bracket.
                      </p>
                    )}
                  </div>

                </div>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default Reports;